/**
 * STT — 외부 API 탭의 셋째 보기 (2026-09-21 · 시안 ①~④ 승인).
 *
 * ## ★★★ 상태는 셋이고, ③으로 가는 길은 «연결 시험»뿐이다
 *
 *   | | 뜻 | 관리자가 할 일 |
 *   |---|---|---|
 *   | ① 미설정 | 끝점도 키도 없다 | 등록한다 |
 *   | ② 설정됨 · **연결 대기** | 넣을 것은 다 넣었고 아직 시험 안 했다 | (엔진이 없던 동안은) 할 일이 없다 |
 *   | ③ 설정됨 · 동작 중 | 연결 시험이 **성공**했다 | — |
 *
 * ⛔ **저장만으로 ③이 되지 않는다.** 실측(2026-09-21): 붙은 서버는 **`/health` 가 키 없이 200** 이고
 * **전사만 401** 이다. 이 규칙이 없으면 **키가 틀려도 바로 「동작 중」**으로 올라간다.
 *
 * ## ⚠ 서버가 아직 안 섰을 때 — 「누르면 아무 일 없는 단추」를 만들지 않는다
 *
 * ✅ 마이그레이션(`config` · `key_last4`)과 STT 어댑터는 **들어왔다**(2026-09-24 확인 —
 * `lib/external-api/stt-adapters.ts:60` · `prisma/schema.prisma` `key_last4`).
 * 그래도 이 잠금을 남겨 둔다 — **서버가 STT 어댑터를 안다고 말할 때만** 편집을 연다.
 * 깃발이 아니라 **서버가 준 목록(`adapters`)을 보고** 정하므로, 되돌린 판이나 배포 사이 창에서도
 * 「눌러도 서버가 거절하는 단추」가 생기지 않는다.
 *
 * ## 왜 표를 날씨와 따로 그리나
 *
 * 칸이 다르다(끝점 · 모델 · 타임아웃). 한 표에 섞으면 「오늘 사용」·「한도」가 STT 줄에서 **늘 빈칸**이 되고,
 * 늘 비어 있는 칸은 사람이 **안 읽게** 된다.
 */
import { AlertTriangle, Globe } from '../../../icons';
import { readSttConfig, type ExternalApiAdapter, type ExternalApiProvider } from '../../../api/externalApis';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';
import { STT_PATH } from './sttEndpoint';

/** 상태 셋 — 화면이 쓰는 이름. 서버 칸 둘(`key_state` · `last_probe_status`)에서 만든다. */
export type SttState = 'unset' | 'waiting' | 'live' | 'failed';

/**
 * ★ 판정을 한 곳에서만 만든다 — 줄 배지와 KPI 가 다른 말을 하지 않게.
 * ⛔ `enabled` 는 여기 안 섞는다 — 「꺼 둔 것」과 「안 되는 것」은 다른 일이다.
 */
export function sttStateOf(p: ExternalApiProvider): SttState {
  const cfg = readSttConfig(p);
  const hasSetup = cfg.endpoint !== '' && (p.key_state === 'stored' || p.env_key_fallback);
  if (!hasSetup) return 'unset';
  if (p.last_probe_status === 'ok') return 'live';
  // ⚠ 「아직 안 해 봤다」와 「해 봤는데 안 됐다」를 뭉치지 않는다 — 할 일이 다르다.
  if (p.last_probe_status === null) return 'waiting';
  return 'failed';
}

const STATE_LABEL: Record<SttState, string> = {
  unset: '미설정',
  waiting: '연결 대기',
  live: '동작 중',
  failed: '연결 실패',
};
const STATE_CLASS: Record<SttState, string> = {
  unset: 'xa-badge--muted',
  waiting: 'xa-badge--warn',
  live: 'xa-badge--ok',
  failed: 'xa-badge--bad',
};
/** 그 상태에서 «무엇을 하면 되는지». 없으면 할 일이 없다는 뜻이다. */
const STATE_TODO: Record<SttState, string | null> = {
  unset: '끝점과 키를 넣습니다',
  waiting: '연결 시험을 해야 「동작 중」이 됩니다',
  live: null,
  failed: '키와 끝점을 다시 봅니다',
};

/** ⛔ 잰 것만 말한다 — 시험은 «무음 WAV» 로 치고, 이 엔진은 `model` 을 무시한다(팝업 머리말). */
const VERDICT_LABEL: Record<string, string> = {
  ok: '전사 끝점이 받음',
  auth_failed: '키 거절',
  shared_quota_exhausted: '한도 소진',
  provider_error: '엔진 오류',
  unreachable: '닿지 못함',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export interface SttPanelProps {
  providers: ExternalApiProvider[];
  adapters: ExternalApiAdapter[];
  loading: boolean;
  busyKey: string | null;
  onTest: (p: ExternalApiProvider) => void;
  onOpen: (p: ExternalApiProvider) => void;
  onAdd: () => void;
}

export function SttPanel({ providers, adapters, loading, busyKey, onTest, onOpen }: SttPanelProps) {
  /** ★ 서버가 부를 줄 아는 STT 엔진이 하나라도 있나 — 이것이 「서버가 섰나」의 답이다. */
  const serverReady = adapters.some((a) => a.category === 'stt');
  const live = providers.filter((p) => sttStateOf(p) === 'live').length;

  return (
    <>
      {!loading && !serverReady && (
        // ⛔ 등록 단추를 살려 두면 눌러도 서버가 거절한다 — 그래서 «왜 못 하는지»를 먼저 말한다.
        <div className="xa-band xa-band--info" role="status">
          <Globe size={18} aria-hidden />
          <span className="xa-band__t">
            <b>서버가 아직 STT 를 부를 줄 모릅니다</b>
            <span>
              엔진 어댑터와 <b>끝점·모델·타임아웃을 담을 칸</b>이 서버에 들어간 뒤에 넣을 수 있습니다 —
              그때 <b>이 화면이 저절로 열립니다.</b> 넣어 둘 값을 미리 준비해 두셔도 됩니다.
            </span>
          </span>
        </div>
      )}

      {!loading && serverReady && providers.length > 0 && live === 0 && (
        <div className="xa-band xa-band--warn" role="status">
          <AlertTriangle size={18} aria-hidden />
          <span className="xa-band__t">
            <b>아직 「동작 중」인 엔진이 없습니다</b>
            <span>
              저장만으로는 동작 중이 되지 않습니다 — <b>연결 시험</b>으로 전사 끝점이 키를 받는지 봐야 합니다.
              그때까지 녹음은 되고 <b>전사만 안 됩니다.</b>
            </span>
          </span>
        </div>
      )}

      <ConsoleListTable
        rows={providers}
        rowKey={(p) => p.provider_key}
        onRowClick={serverReady ? onOpen : undefined}
        empty={loading
          ? '불러오는 중…'
          : serverReady
            ? '등록된 엔진이 없습니다 · 「엔진 등록」으로 시작합니다'
            : '아직 등록할 수 없습니다 — 위 안내를 보십시오'}
        columns={[
          {
            key: 'engine',
            header: '엔진',
            render: (p) => (
              <span className="xa-cell">
                <b>{p.label}</b>
                <span>{p.provider_key}{p.adapter_available ? '' : ' · 부를 수 없음'}</span>
              </span>
            ),
          },
          {
            key: 'endpoint',
            header: '끝점',
            render: (p) => {
              const cfg = readSttConfig(p);
              if (cfg.endpoint === '') return <span className="xa-dim">—</span>;
              return (
                <span className="xa-cell">
                  <b className="xa-grid__addr">{cfg.endpoint}</b>
                  {/* ★ 규약이 정하는 경로를 «보여 준다» — 무엇을 부르는지 감추지 않는다. */}
                  <span>{STT_PATH}</span>
                </span>
              );
            },
          },
          {
            key: 'key',
            header: '키',
            render: (p) => {
              if (p.key_state === 'unreadable') return <span className="xa-badge xa-badge--bad">키를 못 읽음</span>;
              if (p.key_state === 'stored') {
                return (
                  <span className="xa-key">
                    {/* ⚠ 끝 넉 자를 모르는 줄이 있다 — 칸이 생기기 전에 저장된 키. 가짜를 만들지 않는다. */}
                    <b>{p.key_hint ?? '••••'}</b>
                    <span>{p.key_hint ? '저장됨' : '저장됨 · 끝 넉 자 모름'}</span>
                  </span>
                );
              }
              if (p.env_key_fallback) return <span className="xa-badge xa-badge--info">개발키로 돎</span>;
              return <span className="xa-badge xa-badge--muted">키 없음</span>;
            },
          },
          {
            key: 'model',
            header: '모델 · 타임아웃',
            render: (p) => {
              const cfg = readSttConfig(p);
              return (
                <span className="xa-cell">
                  <b>{cfg.model || '—'}</b>
                  <span>{cfg.timeout_sec}초</span>
                </span>
              );
            },
          },
          {
            key: 'state',
            header: '상태',
            render: (p) => {
              const s = sttStateOf(p);
              return (
                <span className="xa-cell">
                  <span className={`xa-badge ${STATE_CLASS[s]}`}>{STATE_LABEL[s]}</span>
                  {STATE_TODO[s] && <span>{STATE_TODO[s]}</span>}
                </span>
              );
            },
          },
          {
            key: 'probe',
            header: '마지막 시험',
            render: (p) => (p.last_probe_status
              ? (
                <span className="xa-cell">
                  <b>{VERDICT_LABEL[p.last_probe_status] ?? p.last_probe_status}</b>
                  <span>{fmtDate(p.last_probe_at)}</span>
                </span>
              )
              : <span className="xa-dim">아직 안 함</span>),
          },
        ]}
        rowActions={(p) => (
          <>
            <button
              type="button"
              className="xa-btn"
              disabled={busyKey === p.provider_key || !p.adapter_available}
              onClick={(e) => { e.stopPropagation(); onTest(p); }}
            >
              {busyKey === p.provider_key ? '시험 중…' : '연결 시험'}
            </button>
            <button type="button" className="xa-btn" onClick={(e) => { e.stopPropagation(); onOpen(p); }}>열기</button>
          </>
        )}
      />
    </>
  );
}
